import { execSync } from 'node:child_process';
import fs from 'node:fs';
import path from 'node:path';
import os from 'node:os';
import { getArgValue, hasFlag } from './auth';
import { command } from './brand.js';
import { promptChoice } from './cli-helpers';
import { pickNodeBinary } from './runner/common';

const MCP_SERVER_NAME = command;

type Target = {
  id: string;
  label: string;
  configPath: () => string;
  configKey: string;
  buildEntry: (canaryPath: string, env?: Record<string, string>) => Record<string, unknown>;
  postInstallMsg?: string;
};

const TARGETS: Target[] = [
  {
    id: 'claude-desktop',
    label: 'Claude Desktop',
    configKey: 'mcpServers',
    configPath: () => {
      if (process.platform === 'win32') {
        return path.join(process.env.APPDATA || '', 'Claude', 'claude_desktop_config.json');
      }
      if (process.platform === 'darwin') {
        return path.join(os.homedir(), 'Library', 'Application Support', 'Claude', 'claude_desktop_config.json');
      }
      return path.join(os.homedir(), '.config', 'Claude', 'claude_desktop_config.json');
    },
    buildEntry: (canaryPath, env) => ({
      command: canaryPath, args: ['mcp'], ...(env ? { env } : {}),
    }),
    postInstallMsg: 'Restart Claude Desktop to activate the MCP server.',
  },
  {
    id: 'claude-code',
    label: 'Claude Code (project .mcp.json)',
    configKey: 'mcpServers',
    configPath: () => path.join(process.cwd(), '.mcp.json'),
    buildEntry: (canaryPath, env) => ({
      command: canaryPath, args: ['mcp'], ...(env ? { env } : {}),
    }),
  },
  {
    id: 'cursor',
    label: 'Cursor',
    configKey: 'mcpServers',
    configPath: () => path.join(os.homedir(), '.cursor', 'mcp.json'),
    buildEntry: (canaryPath, env) => ({
      command: canaryPath, args: ['mcp'], ...(env ? { env } : {}),
    }),
  },
  {
    id: 'windsurf',
    label: 'Windsurf',
    configKey: 'mcpServers',
    configPath: () => path.join(os.homedir(), '.codeium', 'windsurf', 'mcp_config.json'),
    buildEntry: (canaryPath, env) => ({
      command: canaryPath, args: ['mcp'], ...(env ? { env } : {}),
    }),
  },
  {
    id: 'opencode',
    label: 'OpenCode',
    configKey: 'mcp',
    configPath: () => path.join(os.homedir(), '.config', 'opencode', 'opencode.json'),
    buildEntry: (canaryPath, env) => ({
      type: 'local', command: [canaryPath, 'mcp'], enabled: true, ...(env ? { env } : {}),
    }),
  },
];

function printHelp() {
  const targetIds = TARGETS.map((t) => t.id).join(', ');
  console.log(
    [
      'Usage: canary install [--target <id>]',
      '',
      'Install the Canary MCP server into an AI coding tool.',
      '',
      'Options:',
      '  --target <id>   Skip interactive prompt. Valid targets:',
      `                  ${targetIds}`,
      '  -h, --help      Show this help',
      '',
      'Examples:',
      '  canary install                      Interactive target selection',
      '  canary install --target claude-desktop',
      '  canary install --target cursor',
    ].join('\n')
  );
}

function resolveCanaryBinary(): string {
  try {
    const whichCmd = process.platform === 'win32' ? `where ${command}` : `which ${command}`;
    const out = execSync(whichCmd, { encoding: 'utf-8', stdio: ['pipe', 'pipe', 'pipe'] });
    const first = out.split(/\r?\n/).map((s) => s.trim()).find((s) => s.length > 0);
    return first ?? command;
  } catch {
    console.warn(`Warning: could not resolve full path to ${command} binary, using "${command}".`);
    return command;
  }
}

function readConfig(filePath: string): Record<string, unknown> {
  try {
    const raw = fs.readFileSync(filePath, 'utf-8');
    return JSON.parse(raw) as Record<string, unknown>;
  } catch (err: unknown) {
    if ((err as NodeJS.ErrnoException).code === 'ENOENT') {
      return {};
    }
    if (err instanceof SyntaxError) {
      console.error(`Error: ${filePath} contains invalid JSON. Please fix it manually.`);
      process.exit(1);
    }
    throw err;
  }
}

function writeConfig(filePath: string, config: Record<string, unknown>) {
  const dir = path.dirname(filePath);
  fs.mkdirSync(dir, { recursive: true });
  fs.writeFileSync(filePath, JSON.stringify(config, null, 2) + '\n');
}

/**
 * Build a PATH env that puts Node >=18 first.
 * Returns undefined if the current default node is already >=18.
 */
function resolveNodeEnv(): Record<string, string> | undefined {
  const { bin, version } = pickNodeBinary();

  if (!version || version < 18) {
    console.error(
      `Error: Node.js >=18 is required (best found: ${version ? `v${version}` : 'none'}).`
    );
    console.error('Install a supported version, then re-run canary install:');
    console.error('  nvm install 22');
    process.exit(1);
  }

  // Resolve to absolute path
  let nodeBin = bin;
  if (!path.isAbsolute(bin)) {
    try {
      const whichCmd = process.platform === 'win32' ? `where ${bin}` : `which ${bin}`;
      const out = execSync(whichCmd, { encoding: 'utf-8', stdio: ['pipe', 'pipe', 'pipe'] });
      nodeBin = out.split(/\r?\n/).map((s) => s.trim()).find((s) => s.length > 0) ?? bin;
    } catch {
      nodeBin = bin;
    }
  }

  const nodeDir = path.dirname(nodeBin);

  // Check if this node dir is already first in PATH — if so, no env override needed
  const currentPath = process.env.PATH ?? '';
  const firstPathEntry = currentPath.split(path.delimiter)[0];
  if (firstPathEntry === nodeDir) return undefined;

  // Build PATH with the correct node dir first, followed by common system paths
  const systemPaths = ['/usr/local/bin', '/opt/homebrew/bin', '/usr/bin', '/bin', '/usr/sbin', '/sbin'];
  const canaryDir = path.dirname(resolveCanaryBinary());
  const pathEntries = [nodeDir, canaryDir, ...systemPaths].filter(
    (v, i, a) => a.indexOf(v) === i
  );

  console.log(`Using Node.js v${version} from ${nodeDir}`);
  return { PATH: pathEntries.join(path.delimiter) };
}

/**
 * Check if Playwright browsers are installed and print guidance if not.
 */
function checkPlaywright(): void {
  // Check common Playwright browser cache locations
  const cacheDir =
    process.env.PLAYWRIGHT_BROWSERS_PATH ||
    path.join(os.homedir(), '.cache', 'ms-playwright');

  if (!fs.existsSync(cacheDir) || fs.readdirSync(cacheDir).length === 0) {
    console.warn('\nNote: Playwright browsers not found.');
    console.warn('Browser tools require Playwright. Install with:');
    console.warn('  npx playwright install chromium');
    console.warn('(Non-browser MCP tools will work without it.)\n');
    return;
  }

  // Check if chromium specifically exists
  const hasChromium = fs.readdirSync(cacheDir).some((d) => d.startsWith('chromium'));
  if (!hasChromium) {
    console.warn('\nNote: Playwright Chromium not found.');
    console.warn('Browser tools require Chromium. Install with:');
    console.warn('  npx playwright install chromium\n');
  }
}

export async function runInstall(argv: string[]) {
  if (hasFlag(argv, '--help', '-h')) {
    printHelp();
    return;
  }

  // Resolve target
  let target: Target | undefined;
  const targetArg = getArgValue(argv, '--target');

  if (targetArg) {
    target = TARGETS.find((t) => t.id === targetArg);
    if (!target) {
      console.error(`Unknown target "${targetArg}".`);
      console.error(`Valid targets: ${TARGETS.map((t) => t.id).join(', ')}`);
      process.exit(1);
    }
  } else {
    const choice = await promptChoice('Where would you like to install the Canary MCP server?', TARGETS.map((t) => ({ label: t.label, value: t })));
    if (!choice) {
      return;
    }
    target = choice;
  }

  const canaryPath = resolveCanaryBinary();
  const configPath = target.configPath();
  const config = readConfig(configPath);

  // Ensure the config key object exists
  const servers = (config[target.configKey] ?? {}) as Record<string, unknown>;

  // Detect Node >=18 and build env override if needed
  const env = resolveNodeEnv();

  // Always update the entry (in case env or path changed)
  servers[MCP_SERVER_NAME] = target.buildEntry(canaryPath, env);
  config[target.configKey] = servers;

  writeConfig(configPath, config);

  console.log(`\nInstalled Canary MCP server to ${configPath}`);

  // Check Playwright availability and print install guidance if missing
  checkPlaywright();

  if (target.postInstallMsg) {
    console.log(target.postInstallMsg);
  }
}
